import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameBlock, MdFrameBlockChild, MdFrameItem, MdFrameStick} from "./MdFrameBlock";

// Load-time conversion of a frame tree saved before the outline sticks moved into the
// tree (see inline-outline-plan.md). Those blobs keep the outer border in a `perimeter`
// field on the root: top, bottom, left, right, with the bottom under a door split into
// a left run, a threshold and a right run. This module places each of those sticks in
// the tree where it sits in the layout, and turns the threshold stick into the
// threshold item under the doors.

// the legacy outer border of one block.
export type LegacyPerimeter = {
    top?: MdFrameStick;
    bottom?: MdFrameStick | LegacyBottomSplit;
    left?: MdFrameStick;
    right?: MdFrameStick;
};

export type LegacyBottomSplit = {
    kind: "split";
    left: MdFrameStick;
    threshold: MdFrameStick;
    right: MdFrameStick;
};

// what the deserializer collected from a legacy blob while building the tree.
export type LegacyInfo = {
    // the perimeter of each block that had one, by block id.
    perimeters: Map<string, LegacyPerimeter>;
    // the ids of sticks saved with variant "threshold" (the split's threshold, or an
    // inline sill under the door).
    thresholdSticks: Set<string>;
};

export function isLegacyBottomSplit(b: LegacyPerimeter["bottom"]): b is LegacyBottomSplit {
    return !!b && (b as LegacyBottomSplit).kind === "split";
}

type Side = "top" | "bottom" | "left" | "right";
const SIDES: Side[] = ["top", "bottom", "left", "right"];

type SideSticks = Partial<Record<Side, MdFrameStick>>;

// which legacy stick covers each leaf cell on each of its outer sides.
type Cover = Map<MdFrameItem, SideSticks>;

type EdgeLeaf = {item: MdFrameItem; stick: MdFrameStick | null};

// marks the leaves a split bottom covers until the door position is known.
const SPLIT_COVER: MdFrameStick = {id: ""};

// convert `root` in place and return the new root (a wrapper block when a legacy
// stick spans the whole frame across the root's flow). `spec.frameBlockId` supplies
// the ids of the blocks this makes. Every stick keeps its id.
export function convertLegacyFrame(spec: {frameBlockId: number}, root: MdFrameBlock, legacy: LegacyInfo): MdFrameBlock {
    if(legacy.perimeters.size === 0 && legacy.thresholdSticks.size === 0) return root;

    const conv = new Converter(spec, legacy);
    const out = conv.block(root, new Map());
    return out.kind === "block" ? out.block : root;
}

class Converter {
    constructor(private spec: {frameBlockId: number}, private legacy: LegacyInfo) {}

    private nextId(prefix: string): string {
        return `${prefix}:${this.spec.frameBlockId++}`;
    }

    private isThreshold(stick: MdFrameStick): boolean {
        return this.legacy.thresholdSticks.has(stick.id);
    }

    // place the block's legacy sticks: one covering the block's whole edge along its
    // flow goes inline at the start/end; one covering its whole edge across its flow
    // wraps the block; the rest move down to the cells they cover.
    block(block: MdFrameBlock, inherited: Cover): MdFrameBlockChild {
        let cover = inherited;
        const peri = this.legacy.perimeters.get(block.id);
        if(peri) {
            cover = new Map(cover);
            perimeterCover(block, peri, cover);
        }

        const lifted: SideSticks = {};
        for(const side of SIDES) {
            const s = commonStick(block, side, cover);
            if(s && !this.isThreshold(s)) lifted[side] = s;
        }
        cover = withoutSides(block, Object.keys(lifted) as Side[], cover);

        const flowLead: Side = block.kind === "row" ? "left" : "top";
        const flowTrail: Side = block.kind === "row" ? "right" : "bottom";

        const children: MdFrameBlockChild[] = [];
        if(lifted[flowLead]) children.push({kind: "stick", stick: lifted[flowLead]!});
        block.children.forEach((c, i) => {
            if(c.kind === "item") {
                children.push(this.item(c.item, cover));
            } else if(c.kind === "block") {
                children.push(this.block(c.block, cover));
            } else if(this.isInlineThreshold(block, i)) {
                children.push({kind: "item", item: this.thresholdItem()});
            } else {
                children.push(c);
            }
        });
        if(lifted[flowTrail]) children.push({kind: "stick", stick: lifted[flowTrail]!});
        block.children = children;

        const {[flowLead]: _a, [flowTrail]: _b, ...cross} = lifted;
        return this.wrapSides({kind: "block", block}, cross);
    }

    // a legacy sill stick saved inline after the doors in a column.
    private isInlineThreshold(block: MdFrameBlock, index: number): boolean {
        const c = block.children[index];
        const prev = block.children[index - 1];
        return c?.kind === "stick" && this.isThreshold(c.stick)
            && block.kind === "column" && index === block.children.length - 1
            && prev?.kind === "item" && prev.item.kind === "doors";
    }

    private thresholdItem(): MdFrameItem {
        return {id: "threshold", kind: "threshold", size: new FrmVar("threshold:size", 0)};
    }

    // a leaf with its remaining covers. The doors take the threshold as the item under
    // them in a column; every other cover wraps the leaf.
    private item(item: MdFrameItem, cover: Cover): MdFrameBlockChild {
        const sides: SideSticks = {...cover.get(item)};
        let node: MdFrameBlockChild = {kind: "item", item};

        if(item.kind === "doors" && sides.bottom && this.isThreshold(sides.bottom)) {
            delete sides.bottom;
            node = this.wrap(node, "column", undefined, undefined, this.thresholdItem());
        }

        return this.wrapSides(node, sides);
    }

    // wrap a node in blocks holding the sticks on its sides: top/bottom in a column,
    // left/right in a row around that.
    private wrapSides(node: MdFrameBlockChild, sides: SideSticks): MdFrameBlockChild {
        if(sides.top || sides.bottom) node = this.wrap(node, "column", sides.top, sides.bottom);
        if(sides.left || sides.right) node = this.wrap(node, "row", sides.left, sides.right);
        return node;
    }

    // put `node` in a block of `kind` with `lead`/`trail` as its edge sticks. A node
    // that already is a block of that kind takes the sticks itself. `trailItem` goes
    // after the node (the threshold under the doors). The node's flow size moves up to
    // the wrapper, as it describes the slot the wrapper now fills.
    private wrap(node: MdFrameBlockChild, kind: "row" | "column", lead?: MdFrameStick, trail?: MdFrameStick, trailItem?: MdFrameItem): MdFrameBlockChild {
        if(node.kind === "block" && node.block.kind === kind) {
            if(lead) node.block.children.unshift({kind: "stick", stick: lead});
            if(trailItem) node.block.children.push({kind: "item", item: trailItem});
            if(trail) node.block.children.push({kind: "stick", stick: trail});
            return node;
        }

        const id = this.nextId(kind);
        const children: MdFrameBlockChild[] = [];
        if(lead) children.push({kind: "stick", stick: lead});
        children.push(node);
        if(trailItem) children.push({kind: "item", item: trailItem});
        if(trail) children.push({kind: "stick", stick: trail});

        return {kind: "block", block: {id, kind, size: takeSize(node, id), children}};
    }
}

// the node's flow size var, for the wrapper that takes its slot. The node gets a fresh
// unpinned var for its new flow axis.
function takeSize(node: MdFrameBlockChild, wrapperId: string): FrmVar {
    const fresh = (id: string, from?: FrmVar) => new FrmVar(id + ":size", from?.get() ?? 0);

    if(node.kind === "block") {
        const v = node.block.size;
        node.block.size = fresh(node.block.id, v);
        v.rename(wrapperId + ":size");
        return v;
    }
    if(node.kind === "item" && node.item.kind !== "doors" && node.item.size) {
        const v = node.item.size;
        node.item.size = fresh(node.item.id, v);
        v.rename(wrapperId + ":size");
        return v;
    }
    return fresh(wrapperId);
}

// the block's own stick on `side`: a stick child before its first / after its last
// cell, across its flow.
function ownEdgeStick(block: MdFrameBlock, side: Side): MdFrameStick | undefined {
    const first = block.children[0];
    const last = block.children[block.children.length - 1];
    const leading = block.kind === "row" ? side === "left" : side === "top";
    const trailing = block.kind === "row" ? side === "right" : side === "bottom";
    if(leading && first?.kind === "stick") return first.stick;
    if(trailing && last?.kind === "stick") return last.stick;
    return undefined;
}

// the child cells touching `side`: every cell along the block's flow, only the
// first/last cell across it.
function cellsOnSide(block: MdFrameBlock, side: Side): MdFrameBlockChild[] {
    const cells = block.children.filter(c => c.kind !== "stick");
    if(cells.length === 0) return [];
    const across = block.kind === "row"
        ? (side === "left" || side === "right")
        : (side === "top" || side === "bottom");
    if(!across) return cells;
    const leading = side === "top" || side === "left";
    return [leading ? cells[0] : cells[cells.length - 1]];
}

// the leaf cells along one side of the block with the legacy stick covering each, as
// the old renderer resolved it: a block's own edge stick took over its part of the
// edge (no cover there), and a void had no stick.
function collectEdgeLeaves(block: MdFrameBlock, side: Side, cover: MdFrameStick | null, out: EdgeLeaf[]) {
    const next = ownEdgeStick(block, side) ? null : cover;
    for(const c of cellsOnSide(block, side)) {
        if(c.kind === "item") out.push({item: c.item, stick: c.item.kind === "void" ? null : next});
        else if(c.kind === "block") collectEdgeLeaves(c.block, side, next, out);
    }
}

// a split bottom ran its left piece under the cells before the door, the threshold
// under the door and its right piece after it. With no door it covered nothing.
function perimeterCover(block: MdFrameBlock, p: LegacyPerimeter, into: Cover) {
    for(const side of SIDES) {
        const ps = p[side];
        const split = isLegacyBottomSplit(ps) ? ps : null;
        const leaves: EdgeLeaf[] = [];
        collectEdgeLeaves(block, side, split ? SPLIT_COVER : (ps as MdFrameStick | undefined) ?? null, leaves);

        const doorIdx = leaves.findIndex(l => l.item.kind === "doors");
        leaves.forEach((l, i) => {
            let stick = l.stick;
            if(stick === SPLIT_COVER) {
                stick = !split || doorIdx < 0 ? null
                    : i < doorIdx ? split.left
                    : i === doorIdx ? split.threshold
                    : split.right;
            }
            if(!stick) return;
            into.set(l.item, {...into.get(l.item), [side]: stick});
        });
    }
}

// the one stick covering every leaf on the block's `side`, or undefined when the
// leaves differ or any is uncovered.
function commonStick(block: MdFrameBlock, side: Side, cover: Cover): MdFrameStick | undefined {
    const leaves: EdgeLeaf[] = [];
    collectEdgeLeaves(block, side, null, leaves);
    if(leaves.length === 0) return undefined;

    const first = cover.get(leaves[0].item)?.[side];
    if(!first) return undefined;
    return leaves.every(l => cover.get(l.item)?.[side] === first) ? first : undefined;
}

// `cover` without the given sides of the block's edge leaves (the block shows them).
function withoutSides(block: MdFrameBlock, sides: Side[], cover: Cover): Cover {
    const out: Cover = new Map(cover);
    for(const side of sides) {
        const leaves: EdgeLeaf[] = [];
        collectEdgeLeaves(block, side, null, leaves);
        for(const l of leaves) {
            const {[side]: _, ...rest} = out.get(l.item) ?? {};
            out.set(l.item, rest);
        }
    }
    return out;
}
